<template>
  <div class="dashboard">
    <a-spin :spinning="loading">
      <a-row :gutter="16" class="stats-row">
        <a-col :span="6">
          <a-card>
            <a-statistic
              title="商品总数"
              :value="dashboardData.productCount || 0"
              :value-style="{ color: '#3f8600' }"
            >
              <template #prefix>
                <ShopOutlined />
              </template>
            </a-statistic>
          </a-card>
        </a-col>
        
        <a-col :span="6">
          <a-card>
            <a-statistic
              title="库存总量"
              :value="dashboardData.totalInventory || 0"
              :value-style="{ color: '#1890ff' }"
            >
              <template #prefix>
                <DatabaseOutlined />
              </template>
            </a-statistic>
          </a-card>
        </a-col>
        
        <a-col :span="6">
          <a-card>
            <a-statistic
              title="低库存SKU"
              :value="dashboardData.lowSkuCount || 0"
              :value-style="{ color: '#cf1322' }"
            >
              <template #prefix>
                <WarningOutlined />
              </template>
            </a-statistic>
          </a-card>
        </a-col>
        
        <a-col :span="6">
          <a-card>
            <a-statistic
              title="今日入库"
              :value="dashboardData.todayInbound || 0"
              :value-style="{ color: '#722ed1' }"
            >
              <template #prefix>
                <ArrowUpOutlined />
              </template>
            </a-statistic>
          </a-card>
        </a-col>
      </a-row>
    
      <a-row :gutter="16" class="charts-row">
        <a-col :span="12">
          <a-card title="库存分布" class="chart-card">
            <div class="chart-container">
              <div v-if="dashboardData.inventoryDistribution && dashboardData.inventoryDistribution.length > 0">
                <div 
                  v-for="item in dashboardData.inventoryDistribution" 
                  :key="item.categoryName"
                  class="distribution-item"
                >
                  <div class="distribution-header">
                    <span class="category-name">{{ item.categoryName }}</span>
                    <span class="category-count">{{ item.count }} 件 ({{ item.percentage }}%)</span>
                  </div>
                  <a-progress 
                    :percent="item.percentage" 
                    :stroke-color="getProgressColor(item.percentage)"
                    :show-info="false"
                    class="distribution-progress"
                  />
                </div>
              </div>
              <div v-else class="empty-state">
                <a-empty description="暂无库存分布数据" />
              </div>
            </div>
          </a-card>
        </a-col>
      
        <a-col :span="12">
          <a-card title="最新库存动态" class="chart-card">
            <div class="chart-container">
              <div v-if="dashboardData.latestMovements && dashboardData.latestMovements.length > 0">
                <a-timeline>
                  <a-timeline-item
                    v-for="(item, index) in dashboardData.latestMovements"
                    :key="index"
                    :color="getMovementColor(item.movementType)"
                  >
                    <template #dot>
                      <component :is="getMovementIcon(item.movementType)" />
                    </template>
                    <div class="activity-item">
                      <div class="activity-title">
                        {{ item.productName }} {{ translateMovementType(item.movementType) }} {{ item.quantity }} 件
                      </div>
                      <div class="activity-time">{{ item.timeAgo }}</div>
                    </div>
                  </a-timeline-item>
                </a-timeline>
              </div>
              <div v-else class="empty-state">
                <a-empty description="暂无库存动态" />
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    
      <a-row :gutter="16" class="quick-actions-row">
        <a-col :span="24">
          <a-card title="快捷操作">
            <div class="quick-actions">
              <a-button 
                type="primary" 
                size="large" 
                @click="$router.push('/products/create')"
              >
                <template #icon>
                  <PlusOutlined />
                </template>
                创建商品
              </a-button>
              
              <a-button 
                size="large" 
                @click="$router.push('/stock/in')"
              >
                <template #icon>
                  <ArrowUpOutlined />
                </template>
                库存入库
              </a-button>
              
              <a-button 
                size="large" 
                @click="$router.push('/stock/out')"
              >
                <template #icon>
                  <ArrowDownOutlined />
                </template>
                库存出库
              </a-button>
              
              <a-button 
                size="large" 
                @click="$router.push('/stock/adjust')"
              >
                <template #icon>
                  <SettingOutlined />
                </template>
                库存调整
              </a-button>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  ShopOutlined,
  DatabaseOutlined,
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
  SettingOutlined
} from '@ant-design/icons-vue'
import { dashboardApi } from '@/api'

export default {
  name: 'Dashboard',
  components: {
    ShopOutlined,
    DatabaseOutlined,
    WarningOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    PlusOutlined,
    SettingOutlined
  },
  setup() {
    const loading = ref(false)
    const dashboardData = ref({})
    
    const loadDashboardData = async () => {
      loading.value = true
      try {
        const result = await dashboardApi.getDashboardStats({})
        dashboardData.value = result.data
      } catch (error) {
        console.error('加载仪表盘数据失败:', error)
        message.error('加载仪表盘数据失败')
      } finally {
        loading.value = false
      }
    }
    
    const getProgressColor = (percentage) => {
      if (percentage >= 80) return '#1890ff'
      if (percentage >= 60) return '#52c41a'
      if (percentage >= 40) return '#faad14'
      return '#722ed1'
    }
    
    const translateMovementType = (movementType) => {
      const translations = {
        'IN': '入库',
        'OUT': '出库', 
        'ADJUST': '库存调整',
        '入库': '入库',
        '出库': '出库',
        '库存调整': '库存调整'
      }
      return translations[movementType] || movementType
    }
    
    const getMovementColor = (movementType) => {
      const translatedType = translateMovementType(movementType)
      switch (translatedType) {
        case '入库':
          return 'green'
        case '出库':
          return 'blue'
        case '库存调整':
          return 'orange'
        default:
          return 'default'
      }
    }
    
    const getMovementIcon = (movementType) => {
      const translatedType = translateMovementType(movementType)
      switch (translatedType) {
        case '入库':
          return 'ArrowUpOutlined'
        case '出库':
          return 'ArrowDownOutlined'
        case '库存调整':
          return 'SettingOutlined'
        default:
          return 'DatabaseOutlined'
      }
    }
    
    onMounted(() => {
      loadDashboardData()
    })
    
    return {
      loading,
      dashboardData,
      getProgressColor,
      translateMovementType,
      getMovementColor,
      getMovementIcon,
      loadDashboardData
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 0;
}

.stats-row {
  margin-bottom: 24px;
}

.charts-row {
  margin-bottom: 24px;
}

.quick-actions-row {
  margin-bottom: 24px;
}

.chart-card {
  height: 450px;
}

.chart-container {
  height: 370px;
  overflow-y: auto;
  padding: 8px;
}

.quick-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.activity-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  min-height: 60px;
  word-wrap: break-word;
  word-break: break-word;
}

.activity-title {
  font-weight: 500;
  margin-bottom: 6px;
  line-height: 1.4;
  padding-right: 16px;
  white-space: normal;
}

.activity-time {
  color: #999;
  font-size: 12px;
  margin-top: auto;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.distribution-item {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
}

.distribution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.category-name {
  font-weight: 500;
  color: #262626;
}

.category-count {
  font-size: 13px;
  color: #666;
}

.distribution-progress {
  margin-bottom: 0;
}
</style>